import { format } from 'date-fns/format';
import { isToday } from 'date-fns/isToday';
import { isTomorrow } from 'date-fns/isTomorrow';
import { differenceInMinutes } from 'date-fns/differenceInMinutes';
import ko from 'knockout';
import MicroModal from 'micromodal';

import { MeetingDto } from './interfaces';
import { R } from './Resources.resx';

class MeetingViewModel {
	public videoRoomKey: string;
	public startTime: Date;
	public departmentName: string;
	private _meetingOpensBeforeMinutes = 20;

	private now = ko.observable(new Date());

	public url = ko.pureComputed(
		(): string =>
			'/preflight?returnurl=' +
			encodeURIComponent('/room/' + this.videoRoomKey),
	);

	public startTimeFormatted = ko.pureComputed((): string => {
		if (isToday(this.startTime))
			return R.Today + ' ' + format(this.startTime, 'HH:mm');

		if (isTomorrow(this.startTime))
			return R.Tomorrow + ' ' + format(this.startTime, 'HH:mm');

		return format(this.startTime, 'dd.MM.yyyy HH:mm');
	});

	public minutesToMeeting = ko.pureComputed((): number => {
		const minutes = differenceInMinutes(this.startTime, this.now());
		return minutes;
	});

	public isTooEarly = ko.pureComputed(
		(): boolean => this.minutesToMeeting() > this._meetingOpensBeforeMinutes,
	);

	public isJoinable = ko.pureComputed((): boolean => !this.isTooEarly());

	public isPassed = ko.pureComputed(
		(): boolean => this.minutesToMeeting() < -20,
	);

	constructor(meetingDto: MeetingDto) {
		this.videoRoomKey = meetingDto.videoRoomKey;
		this.startTime = new Date(meetingDto.startTime);
		this.departmentName = meetingDto.department.name;

		setInterval(() => {
			this.now(new Date());
		}, 10 * 1000);
	}
}

class MeetingsListViewModel {
	private _meetings = ko.observableArray<MeetingViewModel>([]);

	public nextMeeting = ko.pureComputed<MeetingViewModel>(
		(): MeetingViewModel =>
			this._meetings().filter((meeting) => {
				if (meeting.isJoinable() && !meeting.isPassed()) return meeting;
			})[0],
	);

	public pastMeetings = ko.pureComputed(() => {
		return this._meetings().filter((meeting) => {
			if (meeting.isPassed()) return meeting;
		});
	});

	public futureMeetings = ko.pureComputed(() => {
		return this._meetings().filter((meeting) => {
			const nextMeetingKey =
				this.nextMeeting() && this.nextMeeting().videoRoomKey;
			if (!meeting.isPassed() && meeting.videoRoomKey !== nextMeetingKey)
				return meeting;
		});
	});

	public join(meeting: MeetingViewModel): void {
		if (meeting.isTooEarly()) {
			MicroModal.show('too-early', {
				awaitCloseAnimation: true,
				disableFocus: true,
			});
			return;
		}

		document.location.href = meeting.url();
	}

	public isInGreenland = ko.observable(true);

	constructor(meetings: MeetingDto[]) {
		this._meetings(
			meetings.map<MeetingViewModel>((m) => {
				return new MeetingViewModel(m);
			}),
		);
		const now = new Date();
		const greenlandicTime = now.toLocaleString('en-US', {
			timeZone: 'America/Godthab',
		});
		const localTime = now.toLocaleString('en-US');
		this.isInGreenland(greenlandicTime === localTime);
	}
}

export function MeetingsList(meetings: MeetingDto[]): void {
	ko.applyBindings(new MeetingsListViewModel(meetings), document.body);
}
